import { updateClientData, updateClientDataOnManualCheck } from 'axios/clientApi';
import { getSelectList } from 'axios/dictionaryApi';
import useApi from 'hooks/useApi';
import { ClientEditingInfo, ClientGeneral } from 'models/api/client';
import { createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { DropDown } from '@finbeat/react-toolkit';

type Dictionaries = {
    moreData: DropDown[];
    sex: DropDown[];
    maritalStatus: DropDown[];
    numberOfDependents: DropDown[];
    numberOfChildren: DropDown[];
    socialStatus: DropDown[];
    workExperience: DropDown[];
    loanPurpose: DropDown[];
    editingReason: DropDown[];
    clientDocumentTypes: DropDown[];
    typeOfAccommodation: DropDown[];
    timeOfCurrentResidence: DropDown[];
    workingIndustry: DropDown[];
    accountType: DropDown[];
    educationLevel: DropDown[];
    collectionStatus: DropDown[];
};

type FormContextType = {
    orderId: number | null;
    clientInfo: ClientGeneral | null;
    loading: boolean;
    dictionaries: Dictionaries;
    dictLoading: boolean;
    editingReason: string;

    changeInputField: (props: ClientEditingInfo) => Promise<unknown>;
};

type ClientInfoTabContextType = {
    clientId: number | string;
    orderId: number | null;
    clientInfoQueryFn: () => Promise<ClientGeneral>;
    wasOpened: boolean;
    manualCheck?: boolean;
};

const ClientInfoTabContext = createContext<FormContextType>({} as FormContextType);

const ClientInfoTabProvider = ({
    children,
    clientId,
    orderId,
    clientInfoQueryFn,
    wasOpened,
    manualCheck = false,
}: React.PropsWithChildren<ClientInfoTabContextType>) => {
    const api = useApi();
    const { i18n } = useTranslation();

    const [clientInfo, setClientInfo] = useState<ClientGeneral | null>(null);

    const [dictionaries, setDictionaries] = useState<Dictionaries>({
        moreData: [],
        sex: [],
        maritalStatus: [],
        numberOfDependents: [],
        numberOfChildren: [],
        socialStatus: [],
        workExperience: [],
        loanPurpose: [],
        editingReason: [],
        clientDocumentTypes: [],
        typeOfAccommodation: [],
        timeOfCurrentResidence: [],
        workingIndustry: [],
        accountType: [],
        educationLevel: [],
        collectionStatus: []
    });

    const editingReason = useMemo(
        () => (dictionaries.editingReason[0] && dictionaries.editingReason[0].name) ?? '',
        [dictionaries]
    );

    const clientInfoQuery = useQuery('clientInfo', () => clientInfoQueryFn(), {
        onSuccess: (data) => setClientInfo(data),
        enabled: wasOpened,
    });

    const changeInputField = (props: ClientEditingInfo) => {
        return (manualCheck ? updateClientDataOnManualCheck : updateClientData)(api, clientId, {
            ...props,
            orderId: orderId ?? undefined,
        });
    };

    const clientInfoTabProviderDictsQuery = useQuery(
        'clientInfoTabProviderDicts',
        () =>
            getSelectList(
                api,
                [
                    'ClientMoreDataType',
                    'Sex',
                    'MaritalStatus',
                    'NumberOfDependents',
                    'NumberOfChildren',
                    'SocialStatus',
                    'WorkExperience',
                    'LoanPurpose',
                    'EditingReason',
                    'ClientDocumentTypes',
                    'TypeOfAccommodation',
                    'TimeOfCurrentResidence',
                    'WorkingIndustry',
                    'AccountType',
                    'EducationLevel',
                    'ClientCollectionStatus'
                ],
                i18n.language
            ),
        {
            onSuccess: (data) => {
                setDictionaries({
                    moreData: data['ClientMoreDataType'],
                    sex: data['Sex'],
                    maritalStatus: data['MaritalStatus'],
                    numberOfDependents: data['NumberOfDependents'],
                    numberOfChildren: data['NumberOfChildren'],
                    socialStatus: data['SocialStatus'],
                    workExperience: data['WorkExperience'],
                    loanPurpose: data['LoanPurpose'],
                    editingReason: data['EditingReason'],
                    clientDocumentTypes: data['ClientDocumentTypes'],
                    typeOfAccommodation: data['TypeOfAccommodation'],
                    timeOfCurrentResidence: data['TimeOfCurrentResidence'],
                    workingIndustry: data['WorkingIndustry'],
                    accountType: data['AccountType'],
                    educationLevel: data['EducationLevel'],
                    collectionStatus: data['ClientCollectionStatus']
                });
            },
            enabled: !!clientInfo,
        }
    );

    return (
        <ClientInfoTabContext.Provider
            value={{
                orderId,
                clientInfo,
                loading: clientInfoQuery.isFetching,
                dictionaries,
                dictLoading: clientInfoTabProviderDictsQuery.isFetching,
                editingReason,
                changeInputField,
            }}
        >
            {children}
        </ClientInfoTabContext.Provider>
    );
};

const useClientInfoTabContext = () => useContext<FormContextType>(ClientInfoTabContext);

export { ClientInfoTabProvider, useClientInfoTabContext };
